import PropTypes from "prop-types";
import { isEmpty, isArray, isUndefined } from "lodash";
import { useTranslation } from "react-i18next";
import { Input, Space, Row, Button, Tooltip } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useRef, useState, useEffect } from "react";

import { Table } from "misc/tables";
import { TextField } from "misc/fields";

const PaginatedTableWidget = ({
    dataSource,
    size = "small",
    isLoading,
    isRefetching,
    isError,
    refetch,
    rowKey,
    columns: propsColumns = [],
    hideSearch,
    empty,
    enableManuallyRefresh,
    extraHeader,
    onTableChange,
    filters, // controlled filters
    ...props
}) => {
    const [t] = useTranslation();
    const tableRef = useRef(null);

    // Filter out columns that have visible: false
    const [columns, setColumns] = useState(propsColumns.filter(col => col.visible !== false));
    const [tableState, setTableState] = useState({
        currentPage: 1,
        pageSize: 20,
        filters: filters || {},
        sorter: null
    });
    const [internalFilters, setInternalFilters] = useState({});

    const handleTableChange = (pagination, filters, sorter, extra) => {
        setInternalFilters(filters);
        setTableState({
            currentPage: pagination.current,
            pageSize: pagination.pageSize,
            filters: { ...tableState.filters, ...filters },
            sorter: sorter
        });
        onTableChange && onTableChange(pagination, filters, sorter, extra);
    };

    useEffect(() => {
        if (filters) {
            setTableState(prev => ({
                ...prev,
                filters: { ...prev.filters, ...filters }
            }));

            const enhanced = columns.map(column => ({
                ...column,
                filteredValue:
                    column.key in filters
                        ? isArray(filters[column.key])
                            ? filters[column.key]
                            : isUndefined(filters[column.key])
                            ? null
                            : [filters[column.key]]
                        : internalFilters[column.key]
            }));
            setColumns(enhanced);
        }
    }, [filters]);

    const filteredData = React.useMemo(() => {
        if (!dataSource) return dataSource;

        let filtered = dataSource;

        // Handle search filter
        if (tableState?.filters?.search?.length) {
            const searchValue = tableState.filters.search[0]?.toLowerCase();
            if (searchValue) {
                filtered = filtered.filter(record => Object.values(record).some(value => String(value).toLowerCase().includes(searchValue)));
            }
        }

        // Handle column filters
        Object.entries(tableState?.filters || {}).forEach(([key, values]) => {
            if (key !== "search" && values?.length) {
                const column = columns.find(col => col.key === key);
                if (column?.onFilter) {
                    filtered = filtered.filter(record => values.some(value => column.onFilter(value, record)));
                }
            }
        });
        return filtered;
    }, [dataSource, tableState?.filters, columns]);
    return (
        <div ref={tableRef}>
            <Space direction="vertical" size={20} style={{ width: "100%" }}>
                {!hideSearch && (
                    <Row justify="space-between">
                        <Input.Search
                            onSearch={value => {
                                setTableState({
                                    ...tableState,
                                    filters: { ...tableState.filters, search: value ? [value] : undefined }
                                });
                            }}
                            placeholder={t("t_search")}
                            style={{ width: 350 }}
                            allowClear
                        />
                        {!isLoading && enableManuallyRefresh && (
                            <Tooltip title={t("t_refresh")}>
                                <Button onClick={() => !isRefetching && refetch()} style={{ cursor: isRefetching ? "not-allowed" : "pointer" }}>
                                    <FontAwesomeIcon icon={["fal", "arrows-rotate"]} spin={isRefetching} />
                                </Button>
                            </Tooltip>
                        )}
                        {extraHeader && extraHeader}
                    </Row>
                )}
                <Table
                    size={size}
                    dataSource={filteredData}
                    rowKey={rowKey}
                    columns={columns}
                    pagination={{
                        current: tableState?.currentPage,
                        pageSize: tableState?.pageSize,
                        defaultPageSize: 20,
                        pageSizeOptions: [10, 20, 50, 100],
                        total: filteredData?.length,
                        showSizeChanger: true,
                        showTotal: (total, range) => <TextField prefix={range.join("-")} value="t_of" suffix={total} />,
                        onShowSizeChange: () => {
                            window.scrollTo({
                                top: 0,
                                left: tableRef.current.offsetTop,
                                behavior: "smooth"
                            });
                        }
                    }}
                    onExpand={(expanded, _) => {
                        if (!expanded) {
                            // Workaround to fix double scroll bars when collapsing a row
                            window.scrollTo({
                                top: document.documentElement.scrollTop + 0.01,
                                behavior: "smooth"
                            });
                        }
                    }}
                    onChange={handleTableChange}
                    loading={isLoading || isRefetching}
                    locale={{ emptyText: empty }}
                    style={{ minHeight: 885 }}
                    {...props}
                />
            </Space>
        </div>
    );
};

PaginatedTableWidget.propTypes = {
    queryKey: PropTypes.string.isRequired,
    dataSource: PropTypes.array,
    queryProps: PropTypes.object,
    extractData: PropTypes.oneOfType([PropTypes.func, PropTypes.string]).isRequired,
    extractCount: PropTypes.oneOfType([PropTypes.func, PropTypes.string]).isRequired,
    columns: PropTypes.arrayOf(PropTypes.object).isRequired,
    rowKey: PropTypes.oneOfType([PropTypes.string, PropTypes.func]).isRequired,
    hideSearch: PropTypes.bool,
    empty: PropTypes.shape({
        title: PropTypes.string,
        subTitle: PropTypes.string
    }),
    enableManuallyRefresh: PropTypes.bool,
    onTableChange: PropTypes.func,
    filters: PropTypes.object
};

export default PaginatedTableWidget;
